import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import MockDataIndicator from '../../../../Components/Common/MockDataIndicator';
import { mockBankAccounts } from '../../../../mockData/paymentGateways';
import AppService from '../../../../Configurations/Appservice';
import moment from 'moment';

// Format currency
const formatCurrency = (amount, currency = 'AED') => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: `${color}.main`, mb: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 40, color: `${color}.main` }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Helper: Transform API response to component format
const transformBankAccount = (account) => {
  return {
    ...account,
    type: account.type || 'OPERATIONS',
    isActive: account.status === 'ACTIVE',
    currentBalance: account.balance || 0,
    currency: 'AED'
  };
};

// Mock recent transactions for a bank account
const getMockTransactions = (accountId) => {
  const transactions = [
    {
      id: 'txn-001',
      date: moment().subtract(1, 'days').toISOString(),
      description: 'Customer Topup - ABC Corporation',
      type: 'CREDIT',
      amount: 5000.00,
      balance: 1255000.00,
    },
    {
      id: 'txn-002',
      date: moment().subtract(2, 'days').toISOString(),
      description: 'Service Partner Payment - Emirates Services',
      type: 'DEBIT',
      amount: 3500.00,
      balance: 1250000.00,
    },
    {
      id: 'txn-003',
      date: moment().subtract(3, 'days').toISOString(),
      description: 'Customer Topup - Tech Solutions LLC',
      type: 'CREDIT',
      amount: 8000.00,
      balance: 1253500.00,
    },
    {
      id: 'txn-004',
      date: moment().subtract(5, 'days').toISOString(),
      description: 'Channel Partner Commission',
      type: 'DEBIT',
      amount: 450.00,
      balance: 1245500.00,
    },
    {
      id: 'txn-005',
      date: moment().subtract(7, 'days').toISOString(),
      description: 'Customer Topup - Global Industries',
      type: 'CREDIT',
      amount: 12000.00,
      balance: 1245950.00,
    },
  ];

  return transactions;
};

// Bank Account Detail Row Component
const BankAccountDetailRow = ({ account }) => {
  const transactions = getMockTransactions(account.id);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
      <Grid container spacing={3}>
        {/* Left Column - Account Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Account Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Bank Name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account.bankName}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Account Number
              </Typography>
              <Typography variant="body2">{account.accountNumber}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                IBAN
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {account.iban}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                SWIFT Code
              </Typography>
              <Typography variant="body2">{account.swiftCode}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Branch
              </Typography>
              <Typography variant="body2">{account.branchName}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Account Type
              </Typography>
              <Typography variant="body2">
                {account.type.replace(/_/g, ' ')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Currency
              </Typography>
              <Typography variant="body2">{account.currency}</Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right Column - Recent Transactions */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Recent Transactions (Last 7 days)
          </Typography>

          <TableContainer sx={{ maxHeight: 350, backgroundColor: '#fff', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id} hover>
                    <TableCell>
                      <Typography variant="caption">
                        {moment(txn.date).format('DD MMM')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{txn.description}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bold',
                          color: txn.type === 'CREDIT' ? 'success.main' : 'error.main',
                        }}
                      >
                        {txn.type === 'CREDIT' ? '+' : '-'} {formatCurrency(txn.amount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

// Bank Account Card Component
const BankAccountCard = ({ account, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Card sx={{ mb: 1.5, borderRadius: 1.5, boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
            {/* Left Section: Account Info */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {/* Header Row: Name and Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: '600', fontSize: '0.95rem' }}>
                  {account.accountName}
                </Typography>
                <Chip
                  icon={account.isActive ? <ActiveIcon /> : <InactiveIcon />}
                  label={account.isActive ? 'Active' : 'Inactive'}
                  color={account.isActive ? 'success' : 'default'}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>

              {/* Bank Info Row */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {account.bankName} • {account.type.replace(/_/g, ' ')}
              </Typography>

              {/* Balance and Account Number Row */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
                    Balance
                  </Typography>
                  <Typography sx={{ fontWeight: '600', fontSize: '0.95rem', color: '#064d51' }}>
                    {formatCurrency(account.currentBalance, account.currency)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
                    Account #
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: '500' }}>
                    {account.accountNumber}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Section: Action Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<EditIcon />}
                onClick={() => onEdit(account)}
                sx={{
                  color: '#064d51',
                  fontSize: '0.75rem',
                  padding: '4px 8px',
                  '&:hover': { bgcolor: 'rgba(6, 77, 81, 0.08)' },
                }}
              >
                EDIT
              </Button>
              <Button
                size="small"
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(account.id)}
                sx={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  padding: '4px 8px',
                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' },
                }}
              >
                DELETE
              </Button>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ ml: 0.5 }}
              >
                {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
        </CardContent>

        {/* Collapsible Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <BankAccountDetailRow account={account} />
        </Collapse>
      </Card>
    </>
  );
};

// Main Component
const BankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [isMockData, setIsMockData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [error, setError] = useState(null);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [todayInflow, setTodayInflow] = useState(0);
  const [formData, setFormData] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    iban: '',
    swiftCode: '',
    type: 'OPERATIONS',
    isActive: true,
  });

  useEffect(() => {
    fetchBankAccounts();
    fetchPendingApprovalsCount();
  }, []);

  const fetchBankAccounts = async () => {
    setLoading(true);
    try {
      console.log('[BankAccounts] Fetching bank accounts from API...');

      // Use AppService which automatically handles token injection
      const data = await AppService.get('/v1/admin/bank-accounts');

      console.log('[BankAccounts] Loaded', data.length, 'accounts from API');

      // Transform backend response to frontend format
      const transformedAccounts = data.map(transformBankAccount);

      setAccounts(transformedAccounts);
      setIsMockData(false);
      setError(null);
    } catch (err) {
      console.error('[BankAccounts] Error fetching accounts:', err);
      setError(err.message || 'Failed to load bank accounts');
      // Fallback to mock data if API fails
      setAccounts(mockBankAccounts);
      setIsMockData(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovalsCount = async () => {
    try {
      console.log('[BankAccounts] Fetching pending approvals count...');
      const response = await AppService.get('/v1/payment/pending-approvals/count');

      // Handle nested response structure
      let countValue = 0;
      if (response) {
        // If response has a 'count' property directly
        if (typeof response.count === 'number') {
          countValue = response.count;
        }
        // If response has a 'data' object with count inside
        else if (response.data && typeof response.data === 'object' && response.data.count) {
          countValue = response.data.count;
        }
        // If response is just a number wrapped in data
        else if (response.data && typeof response.data === 'number') {
          countValue = response.data;
        }
        // If response itself is a number
        else if (typeof response === 'number') {
          countValue = response;
        }
      }

      setPendingApprovalsCount(countValue);
      console.log('[BankAccounts] Pending approvals count:', countValue);
    } catch (err) {
      console.error('[BankAccounts] Error fetching pending approvals count:', err);
      // Set default value, don't fail the entire component
      setPendingApprovalsCount(0);
    }
  };

  // Handle opening dialog for add or edit
  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        bankName: account.bankName,
        accountName: account.accountName,
        accountNumber: account.accountNumber,
        iban: account.iban,
        swiftCode: account.swiftCode,
        type: account.type,
        isActive: account.isActive,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        bankName: '',
        accountName: '',
        accountNumber: '',
        iban: '',
        swiftCode: '',
        type: 'OPERATIONS',
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
    setError(null);
  };

  const handleSaveBank = async () => {
    // Validation
    if (!formData.bankName || !formData.accountNumber) {
      setError('Bank Name and Account Number are required');
      return;
    }

    setLoading(true);
    try {
      if (editingAccount) {
        // Update existing bank via AppService
        const updatePayload = {
          bankName: formData.bankName,
          accountName: formData.accountName,
          accountNumber: formData.accountNumber,
          iban: formData.iban,
          swiftCode: formData.swiftCode,
          type: formData.type,
          status: formData.isActive ? 'ACTIVE' : 'INACTIVE'
        };

        // Create axios request config for PUT
        const config = { data: updatePayload };
        const updatedBank = await AppService.put(`/v1/admin/bank-accounts/${editingAccount.id}`, updatePayload, config);

        const updatedAccounts = accounts.map(acc =>
          acc.id === editingAccount.id ? transformBankAccount(updatedBank) : acc
        );
        setAccounts(updatedAccounts);
        console.log('[BankAccounts] Bank updated via API:', editingAccount.id);
      } else {
        // Create new bank via AppService
        const createPayload = {
          bankName: formData.bankName,
          accountName: formData.accountName,
          accountNumber: formData.accountNumber,
          iban: formData.iban,
          swiftCode: formData.swiftCode,
          type: formData.type,
          status: formData.isActive ? 'ACTIVE' : 'INACTIVE'
        };

        const newBank = await AppService.post('/v1/admin/bank-accounts', createPayload);
        setAccounts([...accounts, transformBankAccount(newBank)]);
        console.log('[BankAccounts] Bank created via API:', newBank.id);
      }

      setError(null);
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
      console.error('[BankAccounts] Error saving bank:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBank = async (bankId) => {
    if (window.confirm('Are you sure you want to deactivate this bank account?')) {
      setLoading(true);
      try {
        // Deactivate bank via AppService
        await AppService.delete(`/v1/admin/bank-accounts/${bankId}`);

        // Remove from state or mark as inactive
        const updatedAccounts = accounts.filter(acc => acc.id !== bankId);
        setAccounts(updatedAccounts);
        console.log('[BankAccounts] Bank deactivated via API:', bankId);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('[BankAccounts] Error deactivating bank:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading bank accounts...</Typography>
      </Box>
    );
  }

  // Calculate totals (AED only)
  const activeAccounts = accounts.filter((acc) => acc.isActive);
  const inactiveAccounts = accounts.filter((acc) => acc.isActive === false);

  const totalBalanceAED = accounts.reduce((sum, acc) => sum + acc.currentBalance, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Thaklis Bank Accounts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <MockDataIndicator isMock={isMockData} />
          {pendingApprovalsCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<PendingIcon />}
              onClick={() => window.location.hash = '#/admin/finance/payment-approvals'}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                '&:hover': { borderColor: '#f57c00', backgroundColor: 'rgba(255, 152, 0, 0.08)' }
              }}
            >
              View Pending ({pendingApprovalsCount})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#064d51',
              color: '#ffffff',
              '&:hover': { bgcolor: '#043a3e' }
            }}
          >
            Add Bank Account
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMockData && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Mock Data Mode:</strong> Displaying sample bank account data. Backend integration
          pending.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={3}>
          <SummaryCard
            title="Total Balance"
            value={formatCurrency(totalBalanceAED, 'AED')}
            icon={BankIcon}
            color="primary"
            subtitle={`${activeAccounts.length} active accounts`}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <SummaryCard
            title="Total Accounts"
            value={accounts.length}
            icon={BankIcon}
            color="success"
            subtitle={`${activeAccounts.length} active • ${inactiveAccounts.length} inactive`}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', backgroundColor: pendingApprovalsCount > 0 ? '#fff3cd' : '#f5f5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Pending Approvals
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>
                    {pendingApprovalsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting review
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#ffe0b2',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PendingIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Today's Inflow
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 1 }}>
                    AED {todayInflow.toLocaleString('en-AE', { minimumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Customer deposits
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#c8e6c9',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TrendingIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Accounts Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          Active Accounts ({activeAccounts.length})
        </Typography>
        <Divider sx={{ mb: 1.5 }} />

        {activeAccounts.map((account) => (
          <BankAccountCard
            key={account.id}
            account={account}
            onEdit={handleOpenDialog}
            onDelete={handleDeleteBank}
          />
        ))}
      </Box>

      {/* Inactive Accounts Section */}
      {inactiveAccounts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Inactive Accounts ({inactiveAccounts.length})
          </Typography>
          <Divider sx={{ mb: 1.5 }} />

          {inactiveAccounts.map((account) => (
            <BankAccountCard
              key={account.id}
              account={account}
              onEdit={handleOpenDialog}
              onDelete={handleDeleteBank}
            />
          ))}
        </Box>
      )}

      {/* Account Distribution Chart */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Balance Distribution
          </Typography>

          {activeAccounts.map((account) => {
            const percentage = (account.currentBalance / totalBalanceAED) * 100;

            return (
              <Box key={account.id} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{account.accountName}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(account.currentBalance, account.currency)} ({percentage.toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#064d51' }}>
          {editingAccount ? '✏️ Edit Bank Account' : '➕ Add Bank Account'}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {/* Show configured bank info when editing */}
          {editingAccount && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Configured Bank
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    Bank Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {editingAccount.bankName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    Account Type
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {editingAccount.type.replace(/_/g, ' ')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    Account Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                    {editingAccount.accountNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    Status
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    <Chip
                      label={editingAccount.isActive ? 'Active' : 'Inactive'}
                      color={editingAccount.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bankName}
                onChange={(e) => handleFormChange('bankName', e.target.value)}
                placeholder="e.g., Emirates NBD"
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.accountName}
                onChange={(e) => handleFormChange('accountName', e.target.value)}
                placeholder="e.g., Thaklis Operations Account"
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.accountNumber}
                onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                placeholder="e.g., AE00123456789"
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IBAN"
                value={formData.iban}
                onChange={(e) => handleFormChange('iban', e.target.value)}
                placeholder="International Bank Account Number"
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="SWIFT Code"
                value={formData.swiftCode}
                onChange={(e) => handleFormChange('swiftCode', e.target.value)}
                placeholder="e.g., NBADAEAD"
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  label="Account Type"
                >
                  <MenuItem value="OPERATIONS">Operations</MenuItem>
                  <MenuItem value="STRIPE_SETTLEMENT">Stripe Settlement</MenuItem>
                  <MenuItem value="NGENIUS_SETTLEMENT">N-Genius Settlement</MenuItem>
                  <MenuItem value="REGIONAL">Regional</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                }
                label="Account is Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveBank}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#064d51', '&:hover': { bgcolor: '#043a3e' } }}
          >
            {editingAccount ? 'Update' : 'Create'} Bank
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccounts;
